import {
  type AppError,
  InvalidTokenError,
  type Result,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  type UseInfiniteQueryOptions,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseQueryResult,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {filterExistParams} from '../../shared/services/filterExistParams';
import type {ColumnType} from '../entities/columns/constants';
import {
  ColumnCategoriesSchema,
  ColumnCompanySchema,
  ColumnListItemSchema,
  ColumnPostListSchema,
  ColumnTagSchema,
  GetColumnCarouselList,
  GetPostListResponse,
  GetPostResponse,
  type GetPreviewCodeResponse,
  GetPreviewCodeResponseSchema,
  type PostListSchema,
  RankingColumnListSchema as RankedColumnListSchema,
} from '../entities/columns/schema';
import type {GetSearchKeywordResponse} from '../entities/search/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';
import {getSearchKeywordRepository} from './searchKeywordRepository';

// GET /column/posts
export type GetColumnPostList = z.infer<typeof ColumnPostListSchema>;
export type ResultGetColumnList = Result<GetColumnPostList, AppError>;

type GetColumnPostListArgs = {
  version?: string;
  type?: ColumnType;
  category?: string;
  subCategory?: string;
  tags?: string[];
  title?: string;
  companyID?: number;
  page?: number;
  limit?: number;
};

export const getColumnPostList = async ({
  version = DEFAULT_API_VERSION,
  type,
  category,
  subCategory,
  tags,
  title,
  companyID,
  page,
  limit,
}: GetColumnPostListArgs): Promise<ResultGetColumnList> => {
  const params: Partial<{
    [K in keyof GetColumnPostListArgs]: K extends 'tags' ? string[] : string;
  }> = {};

  if (title) {
    params.title = title;
  }

  if (type) {
    params.type = type;
  }

  if (category) {
    params.category = category;
  }

  if (subCategory) {
    params.subCategory = subCategory;
  }

  if (tags) {
    params.tags = tags;
  }

  if (companyID) {
    params.companyID = String(companyID);
  }

  if (page) {
    params.page = String(page);
  }

  if (limit) {
    params.limit = String(limit);
  }

  const path = createPathWithQuery(`/${version}/column/posts`, params, 'after');
  const result: Result<GetColumnPostList, AppError> = await defaultClient.get(
    path,
    ColumnPostListSchema,
  );

  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export const useGetColumnPostList = ({
  version = DEFAULT_API_VERSION,
  type,
  category,
  subCategory,
  tags,
  title,
  companyID,
  page,
  limit,
}: GetColumnPostListArgs): UseQueryResult<ResultGetColumnList, AppError> => {
  return useQuery(
    [
      'getColumnPostList',
      {type, category, subCategory, tags, title, companyID, limit},
    ],
    async () =>
      getColumnPostList({
        version,
        type,
        category,
        subCategory,
        tags,
        title,
        companyID,
        page,
        limit,
      }),
  );
};

export type UseInfiniteGetColumnPostList = {
  version?: string;
  config?: UseInfiniteQueryOptions<
    ResultGetColumnList,
    AppError,
    ResultGetColumnList,
    ResultGetColumnList,
    [string, Omit<GetColumnPostListArgs, 'page'>]
  >;
  queryParam: Omit<GetColumnPostListArgs, 'page'>;
};

export const useInfiniteGetColumnPostList = ({
  version = DEFAULT_API_VERSION,
  queryParam: {
    type,
    category,
    subCategory,
    tags,
    title,
    companyID,
    limit = 500,
  },
  config,
}: UseInfiniteGetColumnPostList): UseInfiniteQueryResult<
  ResultGetColumnList,
  AppError
> => {
  return useInfiniteQuery({
    queryKey: [
      'getColumnPostList',
      {type, category, subCategory, tags, title, companyID, limit},
    ],
    queryFn: async ({pageParam = 1}) =>
      getColumnPostList({
        version,
        type,
        category,
        subCategory,
        tags,
        title,
        companyID,
        page: pageParam,
        limit,
      }),
    getNextPageParam(lastPage, allPages) {
      return lastPage.ok && lastPage.value.posts.length < limit
        ? undefined
        : allPages.length + 1;
    },
    ...config,
  });
};

type getPostListByIDsArgs = {
  columnIds: string[];
  version?: string;
};

// 記事IDを指定して記事一覧を取得
export const getPostListByIDs = async ({
  columnIds,
  version = DEFAULT_API_VERSION,
}: getPostListByIDsArgs): Promise<Result<GetColumnPostList, AppError>> => {
  const path = createPathWithQuery(
    `/${version}/column/posts`,
    {
      ids: columnIds,
    },
    'after',
  );

  const result: Result<GetColumnPostList, AppError> = await defaultClient.get(
    path,
    ColumnPostListSchema,
  );

  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

// GET /column/posts/:columnId
export type IGetPost = {
  cookies: Partial<Record<string, string>>;
  columnId: string;
  previewCode?: string;
  version?: string;
};

export type IGetPostList = {
  queryParams: {
    type?: string;
    category?: string;
    subCategory?: string;
    tags?: string[];
    q?: string;
    companyID?: number;
    page?: number;
    limit?: number;
  };
  version?: string;
};

export type GetPostReturnValueType = Result<GetPostResponse, AppError>;
export type GetPostListReturnValueType = Result<GetPostListResponse, AppError>;
export type EngineerColumn = z.infer<typeof PostListSchema>;

export const getPost = async ({
  cookies,
  columnId,
  previewCode,
  version = DEFAULT_API_VERSION,
}: IGetPost): Promise<GetPostReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetPostResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const params = filterExistParams({previewCode});
    const path = createPathWithQuery(
      `/${version}/column/posts/${columnId}`,
      params,
    );

    return defaultClient.get(path, GetPostResponse);
  };

  const result = await sendApiRequest<GetPostResponse>({
    request,
    cookies,
    version,
    authRequired: false,
    allowReturnError: true,
  });

  return result;
};

export type IUseGetPost = {
  config?: UseQueryOptions<GetPostReturnValueType>;
} & IGetPost;

export const useGetPost = ({
  columnId,
  previewCode,
  version = DEFAULT_API_VERSION,
  config,
}: IUseGetPost) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetPostReturnValueType>(['getPost', columnId], {
    ...config,
    async queryFn() {
      const cookies = parseCookies();
      const result = await getPost({cookies, columnId, previewCode, version});
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

export type GetCompanyPagePostArgs = {
  cookies: Partial<Record<string, string>>;
  companyId: number;
  slug: string;
  previewCode?: string;
  version?: string;
};

export const getCompanyPagePost = async ({
  cookies,
  companyId,
  slug,
  previewCode,
  version = DEFAULT_API_VERSION,
}: GetCompanyPagePostArgs): Promise<GetPostReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetPostResponse, AppError>> => {
    defaultClient.setToken(accessToken);

    const params = filterExistParams({previewCode});
    const path = createPathWithQuery(
      `/${version}/column/posts-company-page/${companyId}/${slug}`,
      params,
    );
    return defaultClient.get(path, GetPostResponse);
  };

  const result = await sendApiRequest<GetPostResponse>({
    request,
    cookies,
    version,
    authRequired: false,
    allowReturnError: true,
  });
  return result;
};

export type UseGetCompanyPagePost = {
  config?: UseQueryOptions<GetPostReturnValueType>;
} & GetCompanyPagePostArgs;

export const useGetCompanyPagePost = ({
  companyId,
  slug,
  previewCode,
  version = DEFAULT_API_VERSION,
  config,
}: UseGetCompanyPagePost) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetPostReturnValueType>(
    ['getCompanyPagePost', companyId, slug],
    {
      ...config,
      async queryFn() {
        const cookies = parseCookies();
        const result = await getCompanyPagePost({
          cookies,
          companyId,
          slug,
          previewCode,
          version,
        });
        if (!result.ok && result.error === InvalidTokenError) {
          updateIsSessionExpired(true);
        }

        return result;
      },
    },
  );
};

export const getPostList = async ({
  queryParams,
  version = DEFAULT_API_VERSION,
}: IGetPostList): Promise<GetPostListReturnValueType> => {
  const path = createPathWithQuery(
    `/${version}/column/posts`,
    {
      ...queryParams,
    },
    'after',
  );
  const result: Result<GetPostListResponse, AppError> = await defaultClient.get(
    path,
    GetPostListResponse,
  );
  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export type IUseGetPostList = {
  config?: UseQueryOptions<GetPostListReturnValueType>;
} & IGetPostList;

export const useGetPostList = ({
  queryParams,
  version = DEFAULT_API_VERSION,
  config,
}: IUseGetPostList) => {
  return useQuery<GetPostListReturnValueType>(['getPostList'], {
    ...config,
    async queryFn() {
      return getPostList({queryParams, version});
    },
  });
};

// GET /column/categories
export type GetColumnCategories = z.infer<typeof ColumnCategoriesSchema>;
export type GetColumnCategoriesResult = Result<GetColumnCategories, AppError>;

export const getColumnCategories = async ({
  version = DEFAULT_API_VERSION,
  slug,
}: {
  version?: string;
  slug?: string;
} = {}): Promise<GetColumnCategoriesResult> => {
  const path = createPathWithQuery(`/${version}/column/categories`, {
    slug,
  });
  const result: Result<GetColumnCategories, AppError> = await defaultClient.get(
    path,
    ColumnCategoriesSchema,
  );

  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export type UseGetColumnCategories = {
  config?: UseQueryOptions<GetColumnCategoriesResult>;
} & {
  version?: string;
};

export const useGetColumnCategories = ({
  version = DEFAULT_API_VERSION,
  config,
}: UseGetColumnCategories) => {
  return useQuery<GetColumnCategoriesResult>(['getColumnCategories'], {
    ...config,
    async queryFn() {
      return getColumnCategories({version});
    },
  });
};

// GET /column/companies
export const ColumnCompaniesSchema = z.object({
  columnCompanies: z.array(ColumnCompanySchema),
  pagination: z.object({
    totalCount: z.number(),
    limit: z.number(),
    currentPage: z.number(),
    totalPages: z.number(),
  }),
});
export type GetColumnCompanies = z.infer<typeof ColumnCompaniesSchema>;
export type GetCompaniesResult = Result<GetColumnCompanies, AppError>;

export type GetColumnCompaniesArgs = {
  version?: string;
  limit: number;
  page: number;
  industryIDs?: number[];
  orderBy?: 'followers';
};
export const getColumnCompanies = async ({
  version = DEFAULT_API_VERSION,
  limit,
  page,
  industryIDs,
  orderBy,
}: GetColumnCompaniesArgs): Promise<GetCompaniesResult> => {
  const path = createPathWithQuery(
    `/${version}/column/companies`,
    {
      limit,
      page,
      industryIDs,
      orderBy,
    },
    'none',
  );
  const result: Result<GetColumnCompanies, AppError> = await defaultClient.get(
    path,
    ColumnCompaniesSchema,
  );
  if (!result?.ok) {
    throw new Error(result.error);
  }
  return result;
};

export type UseInfiniteColumnCompanies = {
  version?: string;
  config?: UseInfiniteQueryOptions<
    GetCompaniesResult,
    AppError,
    GetCompaniesResult,
    GetCompaniesResult,
    [string, Omit<GetColumnCompaniesArgs, 'page'>]
  >;
  queryParam: Omit<GetColumnCompaniesArgs, 'page' | 'version'>;
};

export const useInfiniteColumnCompanies = ({
  version = DEFAULT_API_VERSION,
  queryParam,
  config,
}: UseInfiniteColumnCompanies): UseInfiniteQueryResult<
  GetCompaniesResult,
  AppError
> => {
  const {limit, industryIDs, orderBy} = queryParam;
  return useInfiniteQuery({
    queryKey: ['getColumnCompanies', {limit}],
    queryFn: async ({pageParam = 1}) =>
      getColumnCompanies({
        version,
        page: pageParam,
        limit,
        industryIDs,
        orderBy,
      }),
    getNextPageParam(lastPage, allPages) {
      return lastPage.ok &&
        lastPage.value.pagination.currentPage <
          lastPage.value.pagination.totalPages
        ? allPages.length + 1
        : undefined;
    },
    ...config,
  });
};

// GET /column/company/[id]
export const GetColumnsByCompanySchema = z.object({
  nativeAd: z.object({
    count: z.number(),
    nativeAdsPosts: ColumnListItemSchema.array(),
  }),
  companyPage: z.object({
    count: z.number(),
    companyPagePosts: ColumnListItemSchema.array(),
  }),
});
export type GetColumnsByCompany = z.infer<typeof GetColumnsByCompanySchema>;
export type GetColumnsByCompanyResult = Result<GetColumnsByCompany, AppError>;
export const getColumnsByCompany = async ({
  version = DEFAULT_API_VERSION,
  companyId,
  limit,
  page,
  keyWord,
}: {
  version?: string;
  companyId: string;
  limit?: number;
  page?: number;
  keyWord?: string;
}): Promise<GetColumnsByCompanyResult> => {
  const path = createPathWithQuery(`/${version}/column/company/${companyId}`, {
    limit,
    page,
    keyWord,
  });
  const result: Result<GetColumnsByCompany, AppError> = await defaultClient.get(
    path,
    GetColumnsByCompanySchema,
  );
  if (!result?.ok) {
    throw new Error(result.error);
  }
  return result;
};

// GET /column/tags/[tag_name]
export const GetColumnTagSchema = z.object({
  tag: ColumnTagSchema,
});
export type GetColumnTag = z.infer<typeof GetColumnTagSchema>;
export type GetColumnTagResult = Result<GetColumnTag, AppError>;
export const getColumnTag = async ({
  version = DEFAULT_API_VERSION,
  tagName,
}: {
  version?: string;
  tagName: string;
}): Promise<GetColumnTagResult> => {
  const tagNameUrlString = encodeURIComponent(tagName);
  const path = createPathWithQuery(
    `/${version}/column/tags/${tagNameUrlString}`,
    {},
  );
  const result: Result<GetColumnTag, AppError> = await defaultClient.get(
    path,
    GetColumnTagSchema,
  );
  return result;
};

// GET /column/ranking
export type RankedColumnList = z.infer<typeof RankedColumnListSchema>;
export type RankedColumnListResult = Result<RankedColumnList, AppError>;
export const RANKING_METRIC_DAILY_PV = 'daily_pv' as const;
export const RANKING_METRIC_WEEKLY_PV = 'weekly_pv' as const;
export const RANKING_METRIC_MONTHLY_PV = 'monthly_pv' as const;

export const RANKING_METRICS = [
  RANKING_METRIC_DAILY_PV,
  RANKING_METRIC_WEEKLY_PV,
  RANKING_METRIC_MONTHLY_PV,
] as const;

export type RankingMetric = (typeof RANKING_METRICS)[number];
type RankedColumnListArgs = {
  version?: string;
  page?: number;
  limit?: number;
  section: RankingMetric;
};

export const getRankedColumnList = async ({
  version = DEFAULT_API_VERSION,
  page,
  limit,
  section = RANKING_METRIC_DAILY_PV,
}: RankedColumnListArgs): Promise<RankedColumnListResult> => {
  const params: Record<string, string> = {};
  if (page) {
    params.page = String(page);
  }

  if (limit) {
    params.limit = String(limit);
  }

  params.section = section;

  const path = createPathWithQuery(`/${version}/column/posts-ranking`, params);
  const result: Result<RankedColumnList, AppError> = await defaultClient.get(
    path,
    RankedColumnListSchema,
  );

  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export type UseRankedColumnList = {
  config?: UseQueryOptions<RankedColumnListResult>;
} & RankedColumnListArgs;

export const useRankedColumnList = ({
  version = DEFAULT_API_VERSION,
  page,
  limit,
  section,
  config,
}: UseRankedColumnList) => {
  return useQuery<RankedColumnListResult>(['rankedColumnList', section], {
    ...config,
    async queryFn() {
      return getRankedColumnList({version, page, limit, section});
    },
  });
};

// GET /column/carousel
export type GetColumnCarouselListType = z.infer<typeof GetColumnCarouselList>;
export type GetColumnCarouselListResult = Result<
  GetColumnCarouselListType,
  AppError
>;

type GetColumnCarouselListArgs = {
  version?: string;
};

export const getColumnCarouselList = async ({
  version = DEFAULT_API_VERSION,
}: GetColumnCarouselListArgs): Promise<GetColumnCarouselListResult> => {
  const result: Result<GetColumnCarouselListType, AppError> =
    await defaultClient.get(
      `/${version}/column/carousel`,
      GetColumnCarouselList,
    );

  if (!result?.ok) {
    throw new Error(result.error);
  }

  return result;
};

export type UseGetColumnCarouselList = {
  config?: UseQueryOptions<GetColumnCarouselListResult>;
} & GetColumnCarouselListArgs;

export const useGetColumnCarouselList = ({
  version = DEFAULT_API_VERSION,
  config,
}: UseGetColumnCarouselList) => {
  return useQuery<GetColumnCarouselListResult>(['getColumnCarouselList'], {
    ...config,
    async queryFn() {
      return getColumnCarouselList({version});
    },
  });
};

type GetPreviewCodeForPostArgs = {
  cookies: Partial<Record<string, string>>;
  columnId: string;
  version?: string;
};

export const getPreviewCodeForPost = async ({
  cookies,
  columnId,
  version = DEFAULT_API_VERSION,
}: GetPreviewCodeForPostArgs): Promise<
  ResultWithOauthToken<GetPreviewCodeResponse, AppError>
> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetPreviewCodeResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.get(
      `/${version}/column/posts/${columnId}/preview-code`,
      GetPreviewCodeResponseSchema,
    );
  };

  const result = await sendApiRequest<GetPreviewCodeResponse>({
    request,
    cookies,
    version,
    authRequired: true,
  });

  return result;
};

type GetPreviewCodeForCompanyPagePostArgs = {
  cookies: Partial<Record<string, string>>;
  companyId: number;
  slug: string;
  version?: string;
};

export const getPreviewCodeForCompanyPagePost = async ({
  cookies,
  companyId,
  slug,
  version = DEFAULT_API_VERSION,
}: GetPreviewCodeForCompanyPagePostArgs): Promise<
  ResultWithOauthToken<GetPreviewCodeResponse, AppError>
> => {
  const request = async (
    accessToken: string,
  ): Promise<Result<GetPreviewCodeResponse, AppError>> => {
    defaultClient.setToken(accessToken);
    return defaultClient.get(
      `/${version}/column/posts-company-page/${companyId}/${slug}/preview-code`,
      GetPreviewCodeResponseSchema,
    );
  };

  const result = await sendApiRequest<GetPreviewCodeResponse>({
    request,
    cookies,
    version,
    authRequired: true,
  });

  return result;
};

/**
 * Infinite scroll for column search
 */
type ColumnSearchResult = Result<
  z.infer<typeof GetSearchKeywordResponse>,
  AppError
>;
type UseInfiniteColumnSearchOptions = {
  keyword: string;
  version?: string;
  config?: UseInfiniteQueryOptions<
    ColumnSearchResult,
    AppError,
    ColumnSearchResult,
    ColumnSearchResult,
    [string, string]
  >;
};

export const useInfiniteColumnSearch = ({
  keyword,
  config,
}: UseInfiniteColumnSearchOptions): UseInfiniteQueryResult<
  ColumnSearchResult,
  AppError
> => {
  return useInfiniteQuery(
    ['columnSearch', keyword],
    async ({pageParam = 1}) => {
      const result = await getSearchKeywordRepository({
        keyword,
        typeString: 'columns',
        page: pageParam,
      });
      return result;
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.ok && lastPage.value.nextPage > 0
          ? lastPage.value.nextPage
          : undefined,
      ...config,
    },
  );
};
